import React from 'react';
import LineChart from './LineChart';
import { weekLabel, weekText, longDate, rangeText } from '../dateRange';

const ENGINE_LABELS = { chat_gpt: 'ChatGPT', google_ai_overview: 'Google AI Overviews' };
const RUN_METRICS = {
  chat_gpt: [['visible', 'נראה (אזכור או ציטוט)'], ['mentioned', 'אוזכר בטקסט התשובה'], ['cited', 'צוטט במקורות']],
  google_ai_overview: [['aio_present', 'הופיע AI Overview'], ['cited_in_aio', 'צוטט בתוך ה-AI Overview'], ['mentioned', 'אוזכר בטקסט']],
};
const AI_ENGINE_LABELS = { ai_total: 'כל מנועי ה-AI', chatgpt: 'ChatGPT', gemini: 'Gemini', perplexity: 'Perplexity', copilot: 'Copilot', claude: 'Claude', other_ai: 'אחר' };
const AI_COLORS = { ai_total: 'var(--accent)', chatgpt: 'var(--green)', gemini: 'var(--yellow)', perplexity: 'var(--red)', copilot: 'var(--text-secondary)', claude: 'var(--text-muted)', other_ai: 'var(--border-light)' };
// Bing Webmaster's API only has ordinary web-search clicks and impressions,
// not AI data, so the section is hidden. The daily collection keeps running;
// switch this back on once Bing exposes its AI Performance data in the API.
const SHOW_BING_SEARCH = false;
const DAILY_STATUS = {
  too_early: 'מוקדם להשוואה — פחות משבוע מאז נקודת הייחוס',
  no_baseline: 'אין מספיק נתונים מלפני נקודת הייחוס',
  no_data: 'אין נתונים שמורים',
};

function shortDate(iso) {
  const p = (iso || '').slice(0, 10).split('-');
  return p.length === 3 ? parseInt(p[2], 10) + '/' + parseInt(p[1], 10) : '-';
}

function num(v) {
  return Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
}

function Badge({ text, tone }) {
  const color = tone === 'up' ? 'var(--green)' : tone === 'down' ? 'var(--red)' : 'var(--text-muted)';
  return <span style={{ fontSize: 11, color: color, border: '1px solid ' + color, borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>{text}</span>;
}

function RunTile({ label, cmp }) {
  const cur = cmp.current.whole ? cmp.current : cmp.baseline;
  let badge = <Badge text="אין עדיין ריצה להשוואה" />;
  if (cmp.delta != null) {
    badge = cmp.within_noise
      ? <Badge text={(cmp.delta > 0 ? '+' : '') + cmp.delta + ' נק׳ · בתחום הרעש'} />
      : <Badge text={(cmp.delta > 0 ? '+' : '') + cmp.delta + ' נק׳'} tone={cmp.delta > 0 ? 'up' : 'down'} />;
  }
  return (
    <div className="section-card" style={{ padding: '14px 16px', marginBottom: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--mono)' }}>
        {cur.part}<span style={{ fontSize: 14, color: 'var(--text-muted)' }}>/{cur.whole}</span>
        {cur.rate != null && <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginInlineStart: 8 }}>{cur.rate}%</span>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 6px' }}>
        {cmp.current.whole ? 'Baseline: ' + cmp.baseline.part + '/' + cmp.baseline.whole + (cmp.baseline.rate != null ? ' (' + cmp.baseline.rate + '%)' : '') : 'זו נקודת ה-Baseline'}
      </div>
      {badge}
    </div>
  );
}

function DailyTile({ label, cmp, field, unit }) {
  if (!cmp) return null;
  const m = cmp.metrics && cmp.metrics[field];
  return (
    <div className="section-card" style={{ padding: '14px 16px', marginBottom: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      {!m ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{DAILY_STATUS[cmp.status] || cmp.status}</div>
      ) : (
        <React.Fragment>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--mono)' }}>
            {num(m.current)}<span style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 6 }}>{unit} ליום</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 6px' }}>
            Baseline: {num(m.previous)} ליום ({cmp.baseline_days} ימים מול {cmp.current_days})
          </div>
          {m.pct == null
            ? <Badge text={m.direction === 'stable' ? 'ללא שינוי' : 'אין בסיס להשוואה באחוזים'} />
            : <Badge text={(m.pct > 0 ? '+' : '') + Math.round(m.pct) + '%'} tone={m.direction === 'stable' ? null : m.direction} />}
        </React.Fragment>
      )}
    </div>
  );
}

function KeyEventsTable({ data }) {
  if (!data) return null;
  const compared = data.status === 'ok';
  const ranged = data.status === 'range';
  const cell = { padding: '6px 4px', fontFamily: 'var(--mono)', width: 110 };
  const head = { padding: '6px 4px', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' };
  return (
    <div className="section-card" style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>המרות לפי מקור AI ולפי אירוע</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        {ranged
          ? 'סך הכול בטווח התאריכים שנבחר (' + data.current_days + ' ימים עם נתוני Analytics).'
          : compared
          ? 'סך הכול ב-' + data.baseline_days + ' הימים שלפני נקודת הייחוס מול ' + data.current_days + ' הימים האחרונים שאחריה.'
          : 'סך הכול ב-' + data.current_days + ' הימים האחרונים. ' + (DAILY_STATUS[data.status] || '') + '.'}
      </div>
      {!data.rows.length ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>לא נרשמו המרות מתנועת AI בתקופה.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={head}>מקור</th>
              <th style={head}>אירוע</th>
              {compared && <th style={head}>Baseline</th>}
              <th style={head}>{ranged ? 'בטווח' : 'עכשיו'}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.engine + ':' + r.event_name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 4px', width: 140 }}>{AI_ENGINE_LABELS[r.engine] || r.engine}</td>
                <td dir="ltr" style={{ padding: '6px 4px', textAlign: 'right' }}>{r.event_name}</td>
                {compared && <td style={cell}>{r.baseline}</td>}
                <td style={cell}>{r.current}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 16 };
const H2 = { fontSize: 16, fontWeight: 700, margin: '8px 0 12px' };

export default function GeoMeasurementReport({ report }) {
  const engines = Object.keys(report.engines || {});
  const gsc = (report.search || {}).gsc || {};
  const ga4 = (report.search || {}).ga4 || {};
  const bing = (report.search || {}).bing || {};
  const anchorText = report.anchor
    ? (report.anchor_is_activity ? 'תחילת פעילות ה-GEO' : 'תחילת המדידה') + ' (' + shortDate(report.anchor) + ')'
    : null;

  const range = report.range || {};
  const ranged = !!(range.start || range.end);
  const noWeeks = <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>אין בטווח הזה שבוע מלא של נתונים.</div>;
  const WEEKLY_NOTE = 'כל נקודה = סכום 7 ימים, מסומנת בטווח התאריכים שלה (יום–יום/חודש). עברו עם העכבר לתאריכים המלאים.';
  const weeklyTitle = text => (
    <React.Fragment>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{text}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{WEEKLY_NOTE}</div>
    </React.Fragment>
  );

  const weeklyChart = (segments, source, field, labelsMap, colors) => {
    const present = segments.filter(s => source[s] && source[s].weekly.length);
    if (!present.length) return null;
    const weeks = source[present[0]].weekly.map(w => w.week_end);
    const marker = report.anchor ? weeks.findIndex(w => w >= report.anchor) : -1;
    return (
      <LineChart labels={weeks.map(weekLabel)} hoverLabels={weeks.map(weekText)} marker={marker >= 0 ? marker : null} markerLabel={report.anchor_is_activity ? 'תחילת פעילות' : 'תחילת מדידה'}
        series={present.map(s => {
          const byWeek = {};
          source[s].weekly.forEach(w => { byWeek[w.week_end] = w[field]; });
          return { label: labelsMap[s] || s, color: colors[s], values: weeks.map(w => (byWeek[w] != null ? byWeek[w] : 0)) };
        })} />
    );
  };

  return (
    <div>
      {ranged && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
          פילוחים לטווח {range.start && range.end ? rangeText(range.start, range.end) : range.start ? 'מ-' + longDate(range.start) : 'עד ' + longDate(range.end)}
          {' · '}{report.shown_runs} ריצות בטווח
        </div>
      )}

      {!engines.length && (
        <div className="section-card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          עדיין אין ריצות. הריצה הראשונה היא נקודת ה-Baseline.
        </div>
      )}

      {engines.map(engine => {
        const e = report.engines[engine];
        const c = e.comparison;
        const dates = e.series.map(s => s.date);
        const marker = report.activity_start_date ? dates.findIndex(d => d >= report.activity_start_date) : -1;
        const aio = engine === 'google_ai_overview';
        return (
          <div key={engine}>
            <h2 style={H2}>{ENGINE_LABELS[engine] || engine}</h2>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '-6px 0 12px' }}>
              {c.current_runs
                ? c.baseline_runs + ' ריצות Baseline מול ' + c.current_runs + ' ריצות אחרונות, מתוך הבדיקות שהחזירו תשובה.'
                : 'יש ' + c.baseline_runs + ' ריצות. ההשוואה תופיע מהריצה הבאה.'}
            </p>
            <div style={GRID}>
              {(RUN_METRICS[engine] || RUN_METRICS.chat_gpt).map(([key, label]) => <RunTile key={key} label={label} cmp={c.metrics[key]} />)}
            </div>
            <div className="section-card" style={{ padding: 16 }}>
              {!e.series.length ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>אין ריצות של המנוע בטווח שנבחר.</div>
              ) : <LineChart suffix="%" labels={dates.map(shortDate)} hoverLabels={dates.map(d => 'ריצה מ-' + longDate(d))} marker={marker >= 0 ? marker : null} markerLabel="תחילת פעילות"
                series={aio ? [
                  { label: 'הופיע AI Overview', color: 'var(--text-muted)', values: e.series.map(s => s.aio_rate) },
                  { label: 'צוטט במקורות', color: 'var(--accent)', values: e.series.map(s => s.citation_rate) },
                ] : [
                  { label: 'נראה', color: 'var(--accent)', values: e.series.map(s => s.visibility_rate) },
                  { label: 'אוזכר בטקסט', color: 'var(--green)', values: e.series.map(s => s.mention_rate) },
                  { label: 'צוטט במקורות', color: 'var(--yellow)', values: e.series.map(s => s.citation_rate) },
                ]} />}
            </div>
            {e.top_domains.length > 0 && (
              <div className="section-card" style={{ padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  הדומיינים שקושרו הכי הרבה ({e.top_domains_checks} תשובות עם מקורות, {ranged ? e.top_domains_runs + ' ריצות בטווח' : e.top_domains_runs + ' ריצות אחרונות'})
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {e.top_domains.map(d => (
                      <tr key={d.domain} style={{ borderTop: '1px solid var(--border)', fontWeight: d.own ? 700 : 400, color: d.own ? 'var(--accent)' : 'inherit' }}>
                        <td dir="ltr" style={{ padding: '6px 4px', textAlign: 'right' }}>{d.domain}{d.own ? ' · האתר' : ''}</td>
                        <td style={{ padding: '6px 4px', width: 90, fontFamily: 'var(--mono)' }}>{d.checks} תשובות</td>
                        <td style={{ padding: '6px 4px', width: 60, fontFamily: 'var(--mono)' }}>{d.share}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <h2 style={H2}>תנועה מ-AI (Google Analytics)</h2>
      {!ga4.total ? (
        <div className="section-card" style={{ padding: 20, fontSize: 13, color: 'var(--text-muted)' }}>אין נתוני Analytics שמורים — בדקו בלשונית ההגדרות.</div>
      ) : (
        <React.Fragment>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '-6px 0 12px' }}>ממוצע יומי: 28 הימים שלפני {anchorText} מול הימים האחרונים שאחריה.</p>
          <div style={GRID}>
            <DailyTile label="סשנים ממנועי AI" cmp={(ga4.ai_total || {}).comparison} field="sessions" unit="סשנים" />
            <DailyTile label="המרות (key events) מ-AI" cmp={(ga4.ai_total || {}).comparison} field="key_events" unit="המרות" />
            <DailyTile label="כלל הסשנים באתר" cmp={ga4.total.comparison} field="sessions" unit="סשנים" />
          </div>
          <div className="section-card" style={{ padding: 16 }}>
            {weeklyTitle('סשנים שבועיים ממנועי AI')}
            {weeklyChart(Object.keys(AI_ENGINE_LABELS), ga4, 'sessions', AI_ENGINE_LABELS, AI_COLORS)
              || <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>לא נרשמה תנועה ממנועי AI בתקופה.</div>}
          </div>
          <div className="section-card" style={{ padding: 16 }}>
            {weeklyTitle('המרות (key events) שבועיות ממנועי AI')}
            {weeklyChart(Object.keys(AI_ENGINE_LABELS), ga4, 'key_events', AI_ENGINE_LABELS, AI_COLORS) || noWeeks}
          </div>
          <KeyEventsTable data={report.key_events} />
        </React.Fragment>
      )}

      <h2 style={H2}>חיפוש בגוגל (Search Console)</h2>
      {!gsc.total ? (
        <div className="section-card" style={{ padding: 20, fontSize: 13, color: 'var(--text-muted)' }}>אין נתוני Search Console שמורים — בדקו בלשונית ההגדרות.</div>
      ) : (
        <React.Fragment>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '-6px 0 12px' }}>
            החתכים לפי שאילתה (ממותג, שיחתי) מבוססים רק על השאילתות ש-Search Console חושף, ולכן מסתכמים בפחות מסך הכול.
          </p>
          <div style={GRID}>
            <DailyTile label="קליקים — סך הכול" cmp={gsc.total.comparison} field="clicks" unit="קליקים" />
            <DailyTile label="חשיפות — סך הכול" cmp={gsc.total.comparison} field="impressions" unit="חשיפות" />
            <DailyTile label="קליקים על שאילתות ממותגות" cmp={(gsc.branded || {}).comparison} field="clicks" unit="קליקים" />
            <DailyTile label="חשיפות בשאילתות שיחתיות" cmp={(gsc.conversational || {}).comparison} field="impressions" unit="חשיפות" />
          </div>
          <div className="section-card" style={{ padding: 16 }}>
            {weeklyTitle('קליקים שבועיים')}
            {weeklyChart(['total', 'non_branded', 'branded'], gsc, 'clicks',
              { total: 'סך הכול', non_branded: 'לא ממותג', branded: 'ממותג' },
              { total: 'var(--accent)', non_branded: 'var(--text-secondary)', branded: 'var(--green)' }) || noWeeks}
          </div>
        </React.Fragment>
      )}

      {SHOW_BING_SEARCH && bing.total && (
        <React.Fragment>
          <h2 style={H2}>חיפוש ב-Bing (Bing Webmaster)</h2>
          <div style={GRID}>
            <DailyTile label="קליקים — סך הכול" cmp={bing.total.comparison} field="clicks" unit="קליקים" />
            <DailyTile label="חשיפות — סך הכול" cmp={bing.total.comparison} field="impressions" unit="חשיפות" />
          </div>
          <div className="section-card" style={{ padding: 16 }}>
            {weeklyTitle('חשיפות שבועיות')}
            {weeklyChart(['total'], bing, 'impressions', { total: 'חשיפות' }, { total: 'var(--accent)' })}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}
